import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Save, Trash2, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function VisaExtraFeeManager({ api, getAuthHeader }) {
  const [extraFees, setExtraFees] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [searchNationality, setSearchNationality] = useState('');
  const [formData, setFormData] = useState({
    nationality_id: '',
    visa_type_id: '',
    extra_fee: 0
  });

  useEffect(() => {
    fetchExtraFees();
    fetchNationalities();
    fetchVisaTypes();
  }, []);

  const fetchExtraFees = async () => {
    try {
      const response = await axios.get(`${api}/visa-extra-fees`);
      setExtraFees(response.data);
    } catch (error) {
      toast.error('Failed to fetch extra fees');
    }
  };

  const fetchNationalities = async () => {
    try {
      const response = await axios.get(`${api}/nationalities`);
      setNationalities(response.data);
    } catch (error) {
      toast.error('Failed to fetch nationalities');
    }
  };

  const fetchVisaTypes = async () => {
    try {
      const response = await axios.get(`${api}/visa-types`);
      setVisaTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch visa types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${api}/visa-extra-fees`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Extra fee saved successfully');
      resetForm();
      fetchExtraFees();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleDelete = async (nationality_id, visa_type_id) => {
    if (!window.confirm('Are you sure you want to delete this extra fee?')) return;
    try {
      await axios.delete(`${api}/visa-extra-fees/${nationality_id}/${visa_type_id}`, {
        headers: getAuthHeader()
      });
      toast.success('Extra fee deleted successfully');
      fetchExtraFees();
    } catch (error) {
      toast.error('Failed to delete extra fee');
    }
  };

  const resetForm = () => {
    setFormData({
      nationality_id: '',
      visa_type_id: '',
      extra_fee: 0
    });
    setShowAdd(false);
  };

  const getNationalityName = (id) => {
    const nat = nationalities.find((n) => n.id === id);
    return nat ? nat.nationality_name : 'Unknown';
  };

  const getVisaTypeName = (id) => {
    const visa = visaTypes.find((v) => v.id === id);
    return visa ? visa.visa_type : 'Unknown';
  };

  const getVisaTypePrice = (id) => {
    const visa = visaTypes.find((v) => v.id === id);
    return visa ? visa.price_aed : 0;
  };

  const filteredFees = extraFees.filter((fee) =>
    getNationalityName(fee.nationality_id).toLowerCase().includes(searchNationality.toLowerCase())
  );

  // Group fees by nationality
  const feesByNationality = {};
  filteredFees.forEach((fee) => {
    const natName = getNationalityName(fee.nationality_id);
    if (!feesByNationality[natName]) {
      feesByNationality[natName] = [];
    }
    feesByNationality[natName].push(fee);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Manage Visa Extra Fees</h2>
          <p className="text-slate-600 mt-1">Set different extra fees per visa type for each nationality</p>
        </div>
        <Button
          data-testid="add-extra-fee-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="btn-primary flex items-center gap-2"
        >
          {showAdd ? 'Cancel' : <><Plus className="w-4 h-4" /> Add Extra Fee</>}
        </Button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <Card>
          <CardHeader>
            <CardTitle>Add Extra Fee for Nationality + Visa Type</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="label-text">Nationality *</label>
                  <Select
                    onValueChange={(value) => setFormData({ ...formData, nationality_id: value })}
                    value={formData.nationality_id}
                  >
                    <SelectTrigger data-testid="extra-fee-nationality-select" className="input-field">
                      <SelectValue placeholder="Select nationality" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {nationalities.map((nat) => (
                        <SelectItem key={nat.id} value={nat.id}>
                          {nat.nationality_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-text">Visa Type *</label>
                  <Select
                    onValueChange={(value) => setFormData({ ...formData, visa_type_id: value })}
                    value={formData.visa_type_id}
                  >
                    <SelectTrigger data-testid="extra-fee-visa-type-select" className="input-field">
                      <SelectValue placeholder="Select visa type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visaTypes.map((visa) => (
                        <SelectItem key={visa.id} value={visa.id}>
                          {visa.visa_type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-text">Extra Fee (AED) *</label>
                  <Input
                    data-testid="extra-fee-amount-input"
                    type="number"
                    step="0.01"
                    value={formData.extra_fee}
                    onChange={(e) => setFormData({ ...formData, extra_fee: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="btn-primary flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Save Extra Fee
                </Button>
                <Button type="button" onClick={resetForm} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div>
        <Input
          data-testid="extra-fee-search-input"
          placeholder="Search by nationality..."
          value={searchNationality}
          onChange={(e) => setSearchNationality(e.target.value)}
          className="input-field max-w-md"
        />
      </div>

      {/* List grouped by nationality */}
      <div className="grid gap-6">
        {Object.keys(feesByNationality).sort().map((nationalityName) => (
          <Card key={nationalityName} className="border-2 border-blue-200">
            <CardHeader className="bg-blue-50">
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <DollarSign className="w-5 h-5" />
                {nationalityName}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid gap-3">
                {feesByNationality[nationalityName].map((fee) => (
                  <div
                    key={`${fee.nationality_id}-${fee.visa_type_id}`}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100"
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{getVisaTypeName(fee.visa_type_id)}</p>
                      <p className="text-sm text-slate-600 mt-1">
                        Base Price: AED {getVisaTypePrice(fee.visa_type_id)} + 
                        <span className="font-bold text-orange-600"> AED {fee.extra_fee}</span> extra = 
                        <span className="font-bold text-blue-700"> AED {getVisaTypePrice(fee.visa_type_id) + fee.extra_fee}</span> total
                      </p>
                    </div>
                    <Button
                      data-testid={`delete-extra-fee-${fee.nationality_id}-${fee.visa_type_id}`}
                      onClick={() => handleDelete(fee.nationality_id, fee.visa_type_id)}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-1 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {Object.keys(feesByNationality).length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500">No extra fees configured yet. Click "Add Extra Fee" to get started.</p>
        </div>
      )}
    </div>
  );
}
